import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, RefreshCw, RotateCcw, Save } from '../../icons';
import { Button, EmptyState, PageHeader, Select } from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import { listShifts, saveShifts, type ShiftDto, type ShiftPersonDto } from '../../api/hr-schedule';
import { monthDays, fillPattern, changedItems, toneOf } from '../../lib/shift-roster';
import './AttendanceAdmin.css';
import './ShiftRoster.css';

/**
 * **교대 편성** — 판 K (2026-08-30).
 *
 * ## 무엇을 하는 화면인가
 *
 * 한 달 표에 **누가 어느 날 어느 조인지**를 찍는다. 조(주간·야간·휴무)는 근무제에서 만들고,
 * 여기서는 **그날의 조만** 정한다. 정한 값은 근태 판정의 **사다리 맨 위**로 들어가서,
 * 그날의 시업 시각·소정·지각 기준이 통째로 그 조를 따른다.
 *
 * ## ★ 왜 「조부터 만들라」고 하나
 *
 * 교대를 안 쓰는 회사가 대부분이다. **새 스위치를 만들지 않고**, 교대 근무제를 한 줄이라도
 * 만든 회사에만 이 화면이 뜬다(메뉴도 그렇게 파생된다). 조가 0개면 표를 그리지 않고
 * **어디서 만드는지**를 알려 준다 — 빈 표를 보여 주면 고장으로 읽힌다.
 *
 * ## ★★ 패턴 채우기가 이 화면의 값어치다
 *
 * 한 사람 30칸을 손으로 찍으면 30번이다. 「주주야야휴휴」를 넣고 **시작 자리를 사람마다 밀면**
 * 교대가 서로 어긋나게 돈다 — 그게 교대의 본질이다.
 */

const ERRORS: Record<string, string> = {
  FORBIDDEN: '이 화면은 근태 담당자만 쓸 수 있습니다.',
  NOT_SHIFT_SCHEDULE: '교대 근무제가 아닌 것이 섞였습니다.',
  USER_NOT_FOUND: '없는 직원이 섞였습니다.',
  RANGE_TOO_LONG: '한 번에 볼 수 있는 기간을 넘었습니다.',
};

const pad = (n: number) => String(n).padStart(2, '0');
const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

export function ShiftRosterPage(): JSX.Element {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [shifts, setShifts] = useState<ShiftDto[]>([]);
  const [people, setPeople] = useState<ShiftPersonDto[]>([]);
  const [draft, setDraft] = useState<Record<string, Record<string, string | null>>>({});
  const [brush, setBrush] = useState<string | null>(null);
  const [pattern, setPattern] = useState<(string | null)[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const days = useMemo(() => monthDays(year, month), [year, month]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const first = `${year}-${pad(month)}-01`;
      const list = monthDays(year, month);
      // 달의 마지막 날 — 목록은 늘 한 줄 이상이라 없는 경우는 없지만, 형이 그것을 모른다.
      const last = list[list.length - 1]?.date ?? first;
      const res = await listShifts(first, last);
      setShifts(res.shifts);
      setPeople(res.people);
      setDraft({});
      setBrush((b) => (res.shifts.some((s) => s.id === b) ? b : res.shifts[0]?.id ?? null));
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(ERRORS[code ?? ''] ?? '불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { void load(); }, [load]);

  const base = useMemo(() => {
    const m: Record<string, Record<string, string>> = {};
    for (const p of people) m[p.userId] = p.days;
    return m;
  }, [people]);

  const valueOf = (userId: string, date: string): string | null => {
    const d = draft[userId];
    if (d && date in d) return d[date] ?? null;
    return base[userId]?.[date] ?? null;
  };

  const setCell = (userId: string, date: string, scheduleId: string | null) => {
    setDone(null);
    setDraft((prev) => ({ ...prev, [userId]: { ...(prev[userId] ?? {}), [date]: scheduleId } }));
  };

  /** 칸을 누르면 고른 조를 찍는다. 이미 그 조면 **지운다**(같은 곳을 두 번 누르면 되돌아온다). */
  const stamp = (userId: string, date: string) => {
    if (!brush) return;
    setCell(userId, date, valueOf(userId, date) === brush ? null : brush);
  };

  const applyPattern = (userId: string, startIndex: number) => {
    if (pattern.length === 0) return;
    const filled = fillPattern(days, pattern, startIndex);
    setDone(null);
    setDraft((prev) => ({ ...prev, [userId]: { ...(prev[userId] ?? {}), ...filled } }));
  };

  const pending = useMemo(() => changedItems(base, draft), [base, draft]);

  const save = async () => {
    if (pending.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await saveShifts(pending);
      setDone(`${res.saved}칸을 세우고 ${res.cleared}칸을 지웠습니다.`);
      await load();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(ERRORS[code ?? ''] ?? '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const toneById = useMemo(() => {
    const m = new Map<string, string>();
    shifts.forEach((s, i) => m.set(s.id, s.isOff ? 'off' : toneOf(i)));
    return m;
  }, [shifts]);

  const shortOf = (id: string | null): string => {
    if (!id) return '';
    const s = shifts.find((x) => x.id === id);
    return s ? s.name.slice(0, 2) : '?';
  };

  return (
    <div className="hra">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/shifts')}
        scope={scopeFor('/erp/attendance/shifts')}
        title="교대 편성"
        description={`${year}년 ${month}월`}
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p>편성한 날은 그 조의 <b>시업 시각이 그날의 판정 기준</b>이 됩니다 — 지각도 소정도
                함께 움직입니다.</p>
              <p>조는 <b>근무제</b> 화면에서 만듭니다. 종류를 <b>교대근무</b>로 골라 「주간」·「야간」·
                「휴무」 같은 조를 두세요. 휴무 조는 <b>소정근로를 0분</b>으로 두면 그날 지각·결근을
                묻지 않습니다.</p>
              <p>칸을 누르면 고른 조가 찍히고 <b>같은 조를 다시 누르면 지워집니다</b>.
                패턴에 조 이름을 쉼표로 넣고 사람 옆 <b>채우기</b>를 누르면 그 달이 한 번에 채워집니다 —
                <b> 시작 자리를 사람마다 밀어</b> 조가 어긋나게 돕니다.</p>
            </>
          ),
        }}
        actions={(
          <>
            <Select
              ariaLabel="연도 선택" size="sm" value={String(year)} onChange={(v) => setYear(Number(v))}
              options={[year + 1, year, year - 1].map((y) => ({ value: String(y), label: `${y}년` }))}
            />
            <Select
              ariaLabel="월 선택" size="sm" value={String(month)} onChange={(v) => setMonth(Number(v))}
              options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}월` }))}
            />
          </>
        )}
      />

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && <p className="hra__ok" role="status"><Check size={14} aria-hidden />{done}</p>}

      {!loading && shifts.length === 0 ? (
        // ★ 빈 표를 그리지 않는다 — 고장으로 읽힌다. 어디서 만드는지 말해 준다.
        <EmptyState
          title="교대 조가 아직 없습니다."
          // ★ 링크를 달지 않는다 — 이 화면은 라우터를 안 타서 `href` 를 주면 **통째 새로고침**이 된다.
          description="근무제 화면에서 종류를 「교대근무」로 골라 주간·야간·휴무 같은 조를 먼저 만들어 주세요. 휴무 조는 소정근로를 0분으로 두면 그날 지각·결근을 묻지 않습니다."
        />
      ) : (
        <>
          <div className="shr__bar">
            <div className="shr__brushes" role="group" aria-label="찍을 조 고르기">
              {shifts.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`shr__brush tone-${toneById.get(s.id)} ${brush === s.id ? 'is-on' : ''}`}
                  aria-pressed={brush === s.id}
                  onClick={() => setBrush(s.id)}
                >
                  {s.name}
                  <span className="hra__dim">
                    {s.isOff ? '휴무' : `${s.startTime}–${s.endTime}`}
                  </span>
                </button>
              ))}
            </div>
            <div className="shr__tools">
              <label className="shr__pattern">
                <span>패턴</span>
                <input
                  type="text"
                  className="hra__reason"
                  aria-label="패턴"
                  placeholder="주간,주간,야간,야간,휴무,휴무"
                  onChange={(e) => setPattern(
                    e.target.value.split(',').map((t) => {
                      const name = t.trim();
                      if (!name) return null;
                      return shifts.find((s) => s.name === name)?.id ?? null;
                    }),
                  )}
                />
              </label>
              <span className="hra__dim">조 이름을 쉼표로 · 빈 칸은 «안 정함»</span>
            </div>
          </div>

          <div className="hra__tablewrap shr__wrap">
            <table className="hra__table shr__table">
              <caption className="hra__caption">
                칸을 누르면 고른 조가 찍히고, 같은 조를 다시 누르면 지워집니다.
                패턴을 넣고 사람 옆 <b>채우기</b>를 누르면 그 달이 한 번에 채워집니다 —
                <b> 시작 자리를 사람마다 밀면</b> 조가 어긋나게 돕니다.
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="shr__who">이름</th>
                  <th scope="col" className="shr__fill">채우기</th>
                  {days.map((d) => (
                    <th
                      key={d.date}
                      scope="col"
                      className={d.weekday === 0 ? 'shr__sun' : d.weekday === 6 ? 'shr__sat' : undefined}
                    >
                      {d.day}
                      <span className="shr__wd">{WEEKDAY[d.weekday]}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={days.length + 2} className="hra__empty">불러오는 중…</td></tr>}
                {!loading && people.length === 0 && (
                  <tr><td colSpan={days.length + 2} className="hra__empty">직원이 없습니다.</td></tr>
                )}
                {!loading && people.map((p, personIndex) => (
                  <tr key={p.userId} className={draft[p.userId] ? 'is-changed' : undefined}>
                    <th scope="row" className="shr__who">
                      {p.name}
                      {p.teamName && <span className="hra__dim"> · {p.teamName}</span>}
                    </th>
                    <td className="shr__fill">
                      <button
                        type="button"
                        className="hra__btn"
                        aria-label={`${p.name} 패턴 채우기`}
                        disabled={pattern.length === 0}
                        onClick={() => applyPattern(p.userId, personIndex)}
                      >
                        채우기
                      </button>
                    </td>
                    {days.map((d) => {
                      const v = valueOf(p.userId, d.date);
                      const tone = v ? toneById.get(v) : null;
                      return (
                        <td key={d.date} className="shr__cell">
                          <button
                            type="button"
                            className={`shr__stamp ${tone ? `tone-${tone}` : ''}`}
                            aria-label={`${p.name} ${d.date}`}
                            onClick={() => stamp(p.userId, d.date)}
                          >
                            {shortOf(v)}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className="hra__foot">
            <p className="hra__note">
              편성한 날은 그 조의 시업 시각이 <b>그날의 판정 기준</b>이 됩니다 — 지각도 소정도 함께 움직입니다.
            </p>
            <div className="hra__actions">
              <Button variant="secondary" iconLeft={<RefreshCw size={14} aria-hidden />} onClick={() => { void load(); }} disabled={busy}>
                다시 불러오기
              </Button>
              <Button variant="secondary" iconLeft={<RotateCcw size={14} aria-hidden />} onClick={() => { setDraft({}); setDone(null); }} disabled={pending.length === 0}>
                되돌리기
              </Button>
              <Button iconLeft={<Save size={14} aria-hidden />} onClick={() => { void save(); }} disabled={busy || pending.length === 0} loading={busy}>
                저장 {pending.length > 0 ? `${pending.length}칸` : ''}
              </Button>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

export default ShiftRosterPage;
